import FunnelPage from '../models/funnel_page'
import FunnelSection from '../models/funnel_section'
import FunnelLookup, { FunnelLookupType } from '../models/funnel_lookup'
import Funnel, { FunnelStep } from '../models/funnel'
import axios from 'axios'
import config from '../config'
import { ProductType } from '../pmd/components/payments/product.type'
import { FunnelsServices } from '../services'
import * as libphonenumber from 'google-libphonenumber'
import { Buffer } from 'buffer'
import { isArray, isObject, snakeCase } from 'lodash'
import { CUSTOM_AGENCIES_PERMISSIONS } from '../apps/saas/constants'

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const currencySymbols = {
  'USD': '$',
  'GBP': '£',
  'EUR': '€',
  'INR': '₹',
}

export const getCurrencySymbol = (code: any) => {
  // @ts-ignore
  return currencySymbols[code] || code + ' '
}

export const restrictUserByAgencyPermission = (locationPermissions, companyId, route) => {
  const customAgencyConfig = CUSTOM_AGENCIES_PERMISSIONS[companyId];
  if(customAgencyConfig && customAgencyConfig.routes.includes(route) && locationPermissions?.custom_changes_enabled) {
    return true;
  }
  return false
}

export const checkIfUserIsSettingFunnelPathAsRoot = (path: string): boolean => {
  console.log('checking url --> ', path)
  if (path && path.trim() === '/') {
    alert(
      'This is a reserved path, please set the root url from the Domain Settings.'
    )
    return true
  }

  return false
}

export const getUniqueFunnelUrl = async (
  domain: string,
  path: string
): Promise<string> => {
  const funnelUrlIsUnique = await checkForUniqueFunnelUrl(domain, path)
  if (funnelUrlIsUnique) {
    return path
  }
  const newFunnelUrl = `${path}-${getRandomNumber()}`
  return getUniqueFunnelUrl(domain, newFunnelUrl)
}

const checkForUniqueFunnelUrl = async (
  domain: string,
  path: string
): Promise<Boolean> => {
  try {
    const response = await axios.post(
      `${config.baseUrl}/funnelbuilder/funnel-unique-url`,
      { domain, path }
    )
    const {
      data: { unique }
    } = response
    return !!unique
  } catch (error) {
    console.error('Error while checking unique funnel url --> ', error)
    throw new Error(error)
  }
}

export const getValidFunnelLookupUrl = (url = '') => {
  if (!url || !url.length) {
    throw new Error(`Invalid URL for funnel lookup --> ${url}`)
  }

  if (url.startsWith('/')) return url

  return `/${url}`
}

function isDomainLookup(lookup: FunnelLookup) {
  if (lookup.type === FunnelLookupType.Domain) return true

  // Currently Root url lookup is added as Step type
  if (
    lookup.domain &&
    lookup.path === '/' &&
    lookup.type === FunnelLookupType.Step
  )
    return true

  return false
}

// @ts-ignore
async function getFunnelLookup({
                                 type,
                                 typeId,
                                 path,
                                 funnelId,
                                 locationId,
                                 domain = ''
                               }) {
  const lookup = new FunnelLookup();
  lookup.type = type;
  lookup.typeId = typeId;
  lookup.path = getValidFunnelLookupUrl(path);
  lookup.funnelId = funnelId;
  lookup.locationId = locationId;
  lookup.domain = domain;
  await lookup.save();
}

// @ts-ignore
export async function createFunnelLookups({funnel, domain, funnelPages}) {
  const promises = [];
  promises.push(getFunnelLookup({
    type: FunnelLookupType.Funnel,
    typeId: funnel.id,
    path:  getValidFunnelLookupUrl(funnel.url),
    funnelId: funnel.id,
    locationId: funnel.locationId,
    domain: domain.url
  }));

  for(const step of funnel.steps) {
    promises.push( getFunnelLookup({
      type: FunnelLookupType.Step,
      typeId: step.id,
      path:  getValidFunnelLookupUrl(step.url),
      funnelId: funnel.id,
      locationId: funnel.locationId,
      domain: domain.url
    }));
  }

  for(const page of funnelPages) {
    promises.push(getFunnelLookup({
      type: FunnelLookupType.Page,
      typeId: page.id,
      path:  getValidFunnelLookupUrl(page.url),
      funnelId: funnel.id,
      locationId: funnel.locationId,
      domain: domain.url
    }));
  }

  await Promise.all(promises)
}

export const updateDomainForAllFunnelLookups = async (
  funnelId: string,
  funnelUrl: string,
  domainName: string,
  createNewLookups: boolean
) => {
  try {
    function getCurrentPathForLookup(lookup: FunnelLookup) {
      console.log('getting instance for lookup --> ', lookup)
      try {
        const { type, typeId } = lookup
        switch (type) {
          case FunnelLookupType.Funnel: {
            return funnelUrl
          }
          case FunnelLookupType.Step: {
            const step = funnel.getStepById(typeId)
            return step.url
          }
          case FunnelLookupType.Page: {
            const page = funnelPages.find(p => p.id === typeId)
            return page.url
          }
        }
      } catch (error) {
        return lookup.path
      }
    }
    // need to update all lookups where this was the funnel id as domain got updated
    // let lookups = await FunnelLookup.getAllByFunnel(funnelId)
    let lookups = []
    const pathUpdatedFor = []

    // const funnelPageIdsToFetch = []

    // Update paths for pages, step, funnel when lookup path is updated
    const funnel = await Funnel.getById(funnelId)
    const funnelPages = await FunnelPage.getByFunnelId(
      funnelId,
      funnel.locationId
    )
    let funnelUpdated = false
    // TAG: LOOKUPS
    // If no lookups found, create new lookups
    if (createNewLookups) {
      const funnelLookup = getNewLookupObject({
        type: FunnelLookupType.Funnel,
        typeId: funnel.id,
        path: getValidFunnelLookupUrl(funnel.url),
        funnelId: funnel.id,
        locationId: funnel.locationId,
        domain: domainName
      })

      const stepLookups = funnel.steps.map(step => {
        const stepLookup = getNewLookupObject({
          type: FunnelLookupType.Step,
          typeId: step.id,
          path: getValidFunnelLookupUrl(step.url),
          funnelId: funnel.id,
          locationId: funnel.locationId,
          domain: domainName
        })

        return stepLookup
      })

      const pageLookups = funnelPages.map(page => {
        const pageLookup = getNewLookupObject({
          type: FunnelLookupType.Page,
          typeId: page.id,
          path: getValidFunnelLookupUrl(page.url),
          funnelId: funnel.id,
          locationId: funnel.locationId,
          domain: domainName
        })

        return pageLookup
      })

      lookups = [funnelLookup, ...stepLookups, ...pageLookups]
    } else {
      const savedLookups = await FunnelLookup.getAllByFunnel(
        funnelId,
        funnel.locationId
      )
      lookups = savedLookups.filter(lookup => !isDomainLookup(lookup))
    }

    for (let i = 0; i < lookups.length; i++) {
      try {
        const lookup = lookups[i]
        // For lookup of funnel, update the path to as set by args
        // const lookupForInstance = getLookupInstance(lookup)
        // console.log('got instance for lookup --> ', lookupForInstance)
        // const currentPath = lookup.type === FunnelLookupType.Funnel ? funnelUrl : lookupForInstance.url
        const currentPath = getCurrentPathForLookup(lookup)
        const uniqueCombinationExists = await checkForUniqueFunnelUrl(
          domainName,
          currentPath
        )
        let newLookupPath = currentPath

        if (!uniqueCombinationExists) {
          const uniqueUrl = await getUniqueFunnelUrl(domainName, currentPath)
          newLookupPath = getValidFunnelLookupUrl(uniqueUrl)

          // if (lookup.type === FunnelLookupType.Page) {
          //   funnelPageIdsToFetch.push(lookup.typeId)
          // }

          pathUpdatedFor.push({
            type: lookup.type,
            typeId: lookup.typeId,
            newPath: newLookupPath
          })
        }
        lookup.path = newLookupPath
        lookup.domain = domainName
        await lookup.save()
      } catch (error) {
        console.error(error)
      }
    }

    // const savingLookups = lookups.map(async (lookup) => {

    //   // For lookup of funnel, update the path to as set by args
    //   const currentPath = lookup.type === FunnelLookupType.Funnel ? funnelUrl : lookup.path
    //   const uniqueCombinationExists = await checkForUniqueFunnelUrl(domainName, currentPath)
    //   let newLookupPath = currentPath

    //   if (!uniqueCombinationExists) {
    //     const uniqueUrl = await getUniqueFunnelUrl(domainName, currentPath)
    //     newLookupPath = getValidFunnelLookupUrl(uniqueUrl)

    //     if (lookup.type === FunnelLookupType.Page) {
    //       funnelPageIdsToFetch.push(lookup.typeId)
    //     }

    //     pathUpdatedFor.push({
    //       type: lookup.type,
    //       typeId: lookup.typeId,
    //       newPath: newLookupPath
    //     })
    //   }
    //   lookup.path = newLookupPath
    //   lookup.domain = domainName
    //   return lookup.save()
    // })

    // await Promise.all(savingLookups)
    console.log(`updated ${lookups.length} lookups`)

    // console.log('path updated for --> ', pathUpdatedFor)

    // const funnelPages = await Promise.all(funnelPageIdsToFetch.map(async (pageId) => await FunnelPage.getById(pageId)))

    const updatingNewPaths = pathUpdatedFor.map(
      async ({ type, typeId, newPath }) => {
        if (type === FunnelLookupType.Page) {
          const page = funnelPages.find(p => p.id === typeId)
          if (page) {
            page.url = newPath
            await page.save()
          }
        } else if (type === FunnelLookupType.Step) {
          const step = funnel.getStepById(typeId)
          if (step) {
            step.url = newPath
            funnel.updateStep(step)
            funnelUpdated = true
          }
        } else if (type === FunnelLookupType.Funnel) {
          funnel.url = newPath
          funnelUpdated = true
        }
      }
    )

    await Promise.all(updatingNewPaths)
    if (funnelUpdated) {
      await funnel.save()
    }

    if (pathUpdatedFor.length > 1) {
      alert(
        'Some of the URLs in the funnel might have been updated due to duplicate urls.'
      )
    }

    // const savingLookups = updatedLookups.map(u => u.save())
  } catch (error) {
    console.error(`error in updating lookups ${error}`)
  }
}

export const getValidUrl = (url = '') => {
  let newUrl = decodeURIComponent(url)
  newUrl = newUrl.trim().replace(/\s/g, '')
  if (/^(:\/\/)/.test(newUrl)) {
    return `http${newUrl}`
  }
  if (!/^(f|ht)tps?:\/\//i.test(newUrl)) {
    return `https://${newUrl}`
  }
  return newUrl
}

export function getRandomNumber(): Number {
  return Math. floor(1000 + Math. random() * 9000)
}

export function getHostAndDomain() {
  let host
  // if (window.location.host.indexOf('localhost') === -1) {
  host = window.location.host
  // } else {
  //     host = 'illinitechs.gohighlevel.com';
  // }

  return {
    domain: host,
    subdomain: host.substr(0, host.indexOf('.') || host.length)
  }
}

export function prepareStringForFunnelUrl(str: string) {
  if (str) {
    return str
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .split(' ')
      .join('-').toLocaleLowerCase()
  }

  return str
}

export function getFunnelUrl(funnelName: string): string {
  // const timestamp = new Date().getTime()
  if (!funnelName) {
    return `/funnel-name-${getRandomNumber()}`
  }
  const cleanedFunnelName = prepareStringForFunnelUrl(funnelName)
  const prefix = cleanedFunnelName.indexOf('/') === 0 ? '' : '/'
  return `${prefix}${cleanedFunnelName}`
}

export function generateFunnelStepUrl(
  stepName: string | undefined,
  stepUrl: string | undefined,
  stepType: string | undefined
): string {
  // const timestamp = new Date().getTime()

  // step url is provided by user, give precedence to it
  if (stepUrl && stepUrl !== '/') {
    const cleanedStepUrl = prepareStringForFunnelUrl(stepUrl)
    const prefix = cleanedStepUrl.includes('/') ? '' : '/'
    return `${prefix}${cleanedStepUrl}`
  }

  if (stepName) {
    const cleanedStepName = prepareStringForFunnelUrl(stepName)
    return `/${cleanedStepName}`
  }

  if (stepType) {
    return `/${stepType}`
  }

  return `/funnel-step`
}

export function generateFunnelPageUrl(
  pageName: string | undefined,
  pageUrl: string | undefined,
  pageType: string | undefined
): string {
  // const timestamp = new Date().getTime()
  // step url is provided by user, give precedence to it
  if (pageUrl && pageUrl !== '/') {
    const cleanedPageUrl = prepareStringForFunnelUrl(pageUrl)
    const prefix = cleanedPageUrl.includes('/') ? '' : '/'
    return `${prefix}${cleanedPageUrl}`
  }

  if (pageName) {
    const cleanedPageName = prepareStringForFunnelUrl(pageName)
    return `/${cleanedPageName}`
  }

  if (pageType) {
    return `/${pageType}`
  }

  return `/funnel-page`
}

export function getFunnelPageName(
  name: string | undefined,
  type: string | undefined
): string {
  const randomNumber = getRandomNumber()
  if (name) {
    return `${name} ${randomNumber}`
  }

  if (type) {
    return `${type} ${randomNumber}`
  }

  return `Funnel Page ${randomNumber}`
}

export function getNewLookupObject({
  type,
  typeId,
  path,
  funnelId,
  locationId,
  domain = ''
}) {
  const newLookup = new FunnelLookup()

  newLookup.type = type
  newLookup.typeId = typeId
  newLookup.path = getValidFunnelLookupUrl(path)
  newLookup.funnelId = funnelId
  newLookup.locationId = locationId
  newLookup.domain = domain

  return newLookup
}

export function createNewFunnelLookup(payload) {
  const newLookup = getNewLookupObject(payload)
  return newLookup.save()
}

export function createNewStepLookup(payload: {
  stepId: string
  funnelId: string
  locationId: string
  domain: string
  url: string
}) {
  const { stepId, funnelId, locationId, domain, url } = payload

  const newLookupPayload = {
    type: FunnelLookupType.Step,
    typeId: stepId,
    path: getValidFunnelLookupUrl(url),
    funnelId,
    locationId,
    domain
  }

  createNewFunnelLookup(newLookupPayload)
}

export async function createVariationPage(
  funnelPage: FunnelPage,
  funnelStep: FunnelStep,
  domainName: string
  ): Promise<FunnelPage> {
  const page = new FunnelPage();
  page.name = `${funnelStep.name}`;
  const currentPath = `/${prepareStringForFunnelUrl(page.name)}-page`;
  const url = await getUniqueFunnelUrl(domainName, currentPath)
  page.url = generateFunnelPageUrl(
    page.name,
    url,
    funnelPage.templateType
  )
  page.locationId = funnelPage.locationId;
  page.funnelId = funnelPage.funnelId;
  page.stepId = funnelPage.stepId;
  page.templateType = funnelPage.templateType;
  await page.save();
  return page;
}

export async function cloneControlPage(
  funnelPage: FunnelPage,
  funnelStep: FunnelStep,
  domainName: string,
  alreadyExistingVariationPageId: string
): Promise<FunnelPage> {
  const params = {
    locationId: funnelPage.locationId,
    stepName: funnelStep.name,
    pageId: funnelPage.id,
    domainName,
    alreadyExistingVariationPageId
  }
  const { data } = await FunnelsServices.cloneControlPage(params)
  const pageId = data.pageId
  return await FunnelPage.getById(pageId)
}

export function cloneFunnelSection(
  funnelSection: FunnelSection
): FunnelSection {
  const cloneSection = new FunnelSection()
  cloneSection.cloneData(funnelSection)
  return cloneSection
}

export const isValidDomainName = (domainStr: string) => {
  // Allow only letters, numbers, hyphens, dots and latin chars
  const re = /^[a-zA-Z0-9-.\u00C0-\u017F]+$/
  return re.test(domainStr);
}

export const isURL = (url: any) => {
  if (!url || !url.length) return

  const regExp = /([--:\w?@%&+~#=]*\.[a-z]{2,4}\/{0,2})((?:[?&](?:\w+)=(?:\w+))+|[--:\w?@%&+~#=]+)?/
  const match = url.match(regExp)
  return match && match.length
}

export const copyTextToClipboard = (text: string) => {
  const textElem = document.createElement('textarea')
  textElem.innerHTML = text
  document.body.appendChild(textElem)
  textElem.select()
  document.execCommand('copy')
  navigator.clipboard.writeText(textElem.value);
  document.body.removeChild(textElem)
}

export const isSafari = () => {
  return navigator.vendor.toLowerCase().indexOf('apple') > -1
}

export const trackGaEvent = (
  eventCategory: string,
  eventAction: string,
  eventLabel?: string,
  eventValue?: Number
) => {
  // Refer to this doc: https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#events
  if (config.googleAnalyticId && ga) {
    ga(
      'send',
      'event',
      eventCategory,
      eventAction,
      eventLabel || '',
      eventValue || 1
    )
  } else {
    console.table({ 'GA Info': '[Only in Dev Mode]', eventCategory, eventAction, eventLabel, eventValue })
  }
  // To test, add these line, in enviroment configurations:
  // googleAnalyticId: 'UA-115177999-2',

}

export const trackGaPageView = (
  pageName: string,
) => {
  // Refer to this doc: https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#events
  if (config.googleAnalyticId && ga) {
    ga('set', 'page', pageName || '');
    ga( 'send','pageview')
  }
  // To test, add these line, in enviroment configurations:
  // googleAnalyticId: 'UA-115177999-2',

}


export const SnapshotAssetMapper: any = {
  text_templates: 'Text & Email Templates',
  email_templates: 'Email Builder Templates',
  funnels: 'Funnels & Websites',
  links: 'Trigger Links',
}

export const trackPendoEvent = (
  eventName: string,
  eventProperties: {[key: string]: any}
) => {
  if (config.mode === 'production') {
    if (window.pendo) {
      window.pendo.track(eventName, eventProperties)
    }
  } else {
    console.table({
      'PENDO Object': '[only in dev mode]',
      eventName,
      ...eventProperties,
    })
  }
}

export const createPriceParams = (
  price: any
) => {
  const priceParams = {
    name: price.name,
    type: price.type,
    currency: price.currency,
    amount: price.amount,
  }

  if (price.hasOwnProperty('description')) priceParams.description = price.description
  if (price.membershipOffers) priceParams.membershipOffers = price.membershipOffers

  if (price.type === ProductType.RECURRING) {
    if (price.recurring) priceParams.recurring = price.recurring
    if (price.trialPeriod) priceParams.trialPeriod = Number(price.trialPeriod)
    if (price.setupFee) priceParams.setupFee = price.setupFee
    if (price.totalCycles) priceParams.totalCycles = price.totalCycles
  }

  return priceParams
}


export function getFriendlyNumber(no: number){
  if(Number.isInteger(no)){
    return no;
  }else{
    return no.toFixed(2)
  }
}

export function wordpressPlans(){
  const plans = [
    'FOUNDER_SPECIAL',
    'UNLIMITED_MONTHLY',
    'UNLIMITED_YEARLY'
  ]
  return {
    plans,
    labelMap:{
      FOUNDER_SPECIAL: 'Founder\'s Special Offer',
      UNLIMITED_MONTHLY: 'Unlimited Monthly Plan',
      UNLIMITED_YEARLY: 'Unlimited Yearly Plan',
      STANDARD: 'Standard'
    }
  }

}

export function sanitizeDomain(domain: string = ''){
  domain = domain.trim();
  domain = domain.replace(/(\s*\.+\s*)+$/g,'');
  domain = domain.replace(/(\s*\/+\s*)+$/g,'');
  return domain;
}

export function stripDomainProtocol(domain: string = ''){
  domain = domain.trim();
  domain = domain.replace(/^https*\:\/\//,'').trim()
  return domain;
}

export function addHttps(domain: string = ''){
  domain = `https://${stripDomainProtocol(domain)}`
  return domain;
}

export function promiseRace(anyPromise:Promise<any>, timeout:number ){
  return Promise.race([
    anyPromise,
    new Promise((_,reject)=>{
      setTimeout(_=>{
        reject(`Timeout exceed ${timeout}`)
      }, timeout)
    })
  ])
}


const toCamel = function (string) {
  return string.replace(/([a-z0-9])[_]([a-z])/g, function (g) {
    return g[0] + g[2].toUpperCase()
  })
}

export const transformToCamelCase = function (data: any) {
  if (data instanceof Date || data instanceof Buffer || data === null || data === undefined || data === {}) {
    return data
  }
  if (data instanceof Array) {
    return data.map((datum) => transformToCamelCase(datum))
  }
  if (data instanceof Object) {
    const transformedObject = {}

    Object.keys(data).forEach((field) => {
      let dataField: any = data[field]

      if (dataField && dataField._seconds !== undefined && dataField._nanoseconds !== undefined) {
        dataField = new Date(dataField._seconds * 1000 + dataField._nanoseconds / 1000000)
      }

      if (dataField instanceof Object) {
        dataField = transformToCamelCase(dataField)
      }

      transformedObject[toCamel(field)] = dataField
    })

    return transformedObject
  }
  return data
}

export function capitalizeFirstLetter(str: string) {
  return str[0].toUpperCase() + str.slice(1);
}


export function parseReportingData(data: any) {
  return data.map((obj: any) => {
    const parsedObj: any = {};
    if (obj.adName) parsedObj.adName = obj.adName;
    if(obj.campaign) {
      parsedObj.campaignId = obj.campaign.id;
      parsedObj.campaignName = obj.campaign.name;
      parsedObj.campaignStatus = obj.campaign.status || '';
      parsedObj.campaignStatusText = obj.campaign.status ? capitalizeFirstLetter(obj.campaign.status.toLowerCase()) : '';
    }
    if(obj.adGroup) {
      parsedObj.adGroupId = obj.adGroup.id;
      parsedObj.adGroupName = obj.adGroup.name;
    }
    if(obj.adGroupAd) {
      parsedObj.adId = obj.adGroupAd.ad ? obj.adGroupAd.ad.id : obj.adGroupAd.id;
      if (obj.adGroupAd.status) parsedObj.adStatus = obj.adGroupAd.status;
      parsedObj.ad = {...(obj.adGroupAd?.ad?.data || obj.adGroupAd?.ad || {})}
      if(!parsedObj.ad.name) parsedObj.ad.name = parsedObj.adName || (parsedObj.ad.headlines ? parsedObj.ad.headlines[0].text : '');
      if(parsedObj.ad.descriptions?.length) {
        parsedObj.ad.description = parsedObj.ad.descriptions.reduce((text: string, curr: any) => {
          return text += text.length ? ` | ${curr.text}` : curr.text
        }, '');
      }
      if(parsedObj.ad.headlines?.length) {
        if(!parsedObj.ad.description) parsedObj.ad.description = '';
        parsedObj.ad.description += parsedObj.ad.headlines.reduce((text: string, curr: any) => {
          return text += text.length ? ` | ${curr.text}` : curr.text
        }, '');
      }
      parsedObj.ad.finalUrl = obj.adGroupAd?.ad?.finalUrls ? obj.adGroupAd.ad.finalUrls[0] : null;
    }
    if(obj.adGroupCriterion && obj.adGroupCriterion.keyword) {
      parsedObj.keyword = obj.adGroupCriterion.keyword.text || '--'
    }
    if(obj.metrics) {
      for(let key of Object.keys(obj.metrics)) {
        parsedObj[key] = obj.metrics[key]
      }
    }
    if (parsedObj.clicks) parsedObj.clicks = parseInt(parsedObj.clicks, 10);
    if (parsedObj.averageCpc) parsedObj.averageCpc = parseInt(parsedObj.averageCpc, 10) / 1000000;
    if (parsedObj.viewThroughConversions) parsedObj.viewThroughConversions = parseInt(parsedObj.viewThroughConversions, 10);
    if (parsedObj.costMicros) parsedObj.costMicros = parseInt(parsedObj.costMicros, 10) / 1000000;
    if (parsedObj.impressions) parsedObj.impressions = parseInt(parsedObj.impressions, 10);
    if (parsedObj.conversions) parsedObj.conversions = parseFloat(parsedObj.conversions);
    if (parsedObj.ctr) parsedObj.ctr = parsedObj.ctr * 100;
    if (parsedObj.conversionsFromInteractionsRate) parsedObj.conversionsFromInteractionsRate =
      parseFloat(parsedObj.conversionsFromInteractionsRate);
    if (parsedObj.costPerConversion) parsedObj.costPerConversion = parseInt(parsedObj.costPerConversion, 10) / 1000000;
    return parsedObj;
  });
}
export function parseReportingBannerData(data: any, customer: any) {
  let currencyCode = customer && customer.currencyCode ? getCurrencySymbol(customer.currencyCode) : ''
  if (data.avg_cpc && data.avg_cpc.hasOwnProperty('value')) {
    data.avg_cpc.value = formatNumber(data.avg_cpc.value)
    data.avg_cpc.prefix =  currencyCode
    data.avg_cpc.suffix = ''
  }
  if (data.conversion_rate && data.conversion_rate.hasOwnProperty('value')) {
    data.conversion_rate.value = formatPercent(data.conversion_rate.value)
  }
  if (data.cost && data.cost.hasOwnProperty('value')) {
    data.cost.value = formatNumber(data.cost.value)
    data.cost.prefix = currencyCode
    data.cost.suffix = ''
  }
  if (data.cost_per_conversion && data.cost_per_conversion.hasOwnProperty('value')) {
    data.cost_per_conversion.value = formatNumber(data.cost_per_conversion.value)
    data.cost_per_conversion.prefix = currencyCode
    data.cost_per_conversion.suffix = ''
  }
  if (data.impressions && data.impressions.hasOwnProperty('value')) {
    data.impressions.value = formatDecimal(data.impressions.value)
  }
  if (data.clicks && data.clicks.hasOwnProperty('value')) {
    data.clicks.value = formatDecimal(data.clicks.value)
  }
  if (data.conversions && data.conversions.hasOwnProperty('value')) {
    data.conversions.value = formatDecimal(data.conversions.value)
  }
  return data;
}

export const formatNumber = (value: any) => {
  return new Intl.NumberFormat(
    navigator.language,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value))
}

export const formatDecimal = (value: any) => {
  return new Intl.NumberFormat(
    navigator.language,
    {
      style: 'decimal'
    }).format(Number(value))
}

export const formatPercent = (value: any) => {
  return new Intl.NumberFormat(
    navigator.language,
    {
      style: 'percent',
      minimumFractionDigits: 2
    }).format(Number(value))
}

export const locationNicheOptions = [
  {
    label: 'Financial Services',
    value: 'finance',
    icon: 'far fa-money-bill-alt',
  },
  {
    label: 'Marketing & Advertising',
    value: 'marketing',
    icon: 'fas fa-bullhorn',
  },
  {
    label: 'Fitness & Lifestyle',
    value: 'fitness',
    icon: 'fas fa-heartbeat',
  },
  {
    label: 'Health & Medical',
    value: 'health',
    icon: 'fas fa-medkit',
  },
  {
    label: 'Online Training & Education',
    value: 'education',
    icon: 'fas fa-university',
  },
  {
    label: 'Real Estate',
    value: 'real_estate',
    icon: 'far fa-building',
  },
  {
    label: 'Home Services',
    value: 'home_services',
    icon: 'fas fa-home',
  },
  {
    label: 'Restaurants',
    value: 'restaurants',
    icon: 'fas fa-pizza-slice',
  },
  {
    label: 'Legal Services',
    value: 'legal',
    icon: 'fas fa-balance-scale',
  },
  {
    label: 'Retail & E-Commerce',
    value: 'retail',
    icon: 'fas fa-truck',
  },
  {
    label: 'Others',
    value: 'others',
    icon: 'fas fa-ellipsis-h',
  },
]

export const A2PCampaignUseCaseOptions = [
  {
    label: 'Low Volume Mixed (Recommended)',
    value: 'LOW_VOLUME'
  },
  {
    label: 'Two-Factor Authentication (2FA)',
    value: '2FA'
  },
  {
    label: 'Account Notification',
    value: 'ACCOUNT_NOTIFICATION'
  },
  {
    label: 'Customer Care',
    value: 'CUSTOMER_CARE'
  },
  {
    label: 'Delivery Notification',
    value: 'DELIVERY_NOTIFICATION'
  },
  {
    label: 'Fraud Alert Messaging',
    value: 'FRAUD_ALERT'
  },
  {
    label: 'Higher Education',
    value: 'HIGHER_EDUCATION'
  },
  {
    label: 'Marketing',
    value: 'MARKETING'
  },
  {
    label: 'Mixed',
    value: 'MIXED'
  },
  {
    label: 'Polling and Voting',
    value: 'POLLING_VOTING'
  },
  {
    label: 'Public Service Announcement',
    value: 'PUBLIC_SERVICE_ANNOUNCEMENT'
  },
  {
    label: 'Security Alert',
    value: 'SECURITY_ALERT'
  },
]

export const A2PStandardCampaignUseCaseOptions = [
  {
    label: 'Mixed (Recommended)',
    value: 'MIXED'
  },
  {
    label: 'Two-Factor Authentication (2FA)',
    value: '2FA'
  },
  {
    label: 'Account Notification',
    value: 'ACCOUNT_NOTIFICATION'
  },
  {
    label: 'Customer Care',
    value: 'CUSTOMER_CARE'
  },
  {
    label: 'Delivery Notification',
    value: 'DELIVERY_NOTIFICATION'
  },
  {
    label: 'Fraud Alert Messaging',
    value: 'FRAUD_ALERT'
  },
  {
    label: 'Higher Education',
    value: 'HIGHER_EDUCATION'
  },
  {
    label: 'Marketing',
    value: 'MARKETING'
  },
  {
    label: 'Polling and Voting',
    value: 'POLLING_VOTING'
  },
  {
    label: 'Public Service Announcement',
    value: 'PUBLIC_SERVICE_ANNOUNCEMENT'
  },
  {
    label: 'Security Alert',
    value: 'SECURITY_ALERT'
  },
]

export const MonthlyMessageVolume = [
  {
    label: '10',
    value: '10'
  },
  {
    label: '100',
    value: '100'
  },
  {
    label: '1,000',
    value: '1,000'
  },
  {
    label: '10,000',
    value: '10,000'
  },
  {
    label: '100,000',
    value: '100,000'
  },
  {
    label: '250,000',
    value: '250,000'
  },
  {
    label: '500,000',
    value: '500,000'
  },
  {
    label: '750,000',
    value: '750,000'
  },
  {
    label: '1,000,000',
    value: '1,000,000'
  },
  {
    label: '5,000,000',
    value: '5,000,000'
  },
  {
    label: '10,000,000+',
    value: '10,000,000+'
  }
]

export const businessType = [
  {
    label: 'Co-operative',
    value: 'Co-operative',
  },
  {
    label: 'Corporation',
    value: 'Corporation',
  },
  {
    label: 'Limited Liability Company Or Sole-Proprietorship',
    value: 'Limited Liability Corporation',
  },
  {
    label: 'Non-profit Corporation',
    value: 'Non-profit Corporation',
  },
  {
    label: 'Partnership',
    value: 'Partnership',
  },
]

export const businessRegistrationType = [
  {
  label: 'USA: Employer Identification Number (EIN)',
  value: 'EIN'
},
  {
    label: 'Canada: Canadian Business Number (CBN)',
    value: 'CBN'
  },
  {
    label: 'Great Britain: Company Number',
    value: 'CN'
  },
  {
    label: 'Australia: Company Number from ASIC (ACN)',
    value: 'ACN'
  },
  {
    label: 'India: Corporate Identity Number',
    value: 'CIN'
  },
  {
    label: 'Estonia: VAT Number',
    value: 'VAT'
  },
  {
    label: 'Israel: Registration Number',
    value: 'RN'
  },
  {
    label: 'Other',
    value: 'Other'
  }
]

export const jobPositionOptions = [
  {
    label: 'Director',
    value: 'Director',
  },
  {
    label: 'GM',
    value: 'GM',
  },
  {
    label: 'VP',
    value: 'VP',
  },
  {
    label: 'CEO',
    value: 'CEO',
  },
  {
    label: 'CFO',
    value: 'CFO',
  },
  {
    label: 'General Counsel',
    value: 'General Counsel',
  },
  {
    label: 'Other',
    value: 'Other',
  },
]

export const businessIndustry = [
  {
  label: 'Automotive',
  value: 'AUTOMOTIVE'
},
  {
    label: 'Agriculture',
    value: 'AGRICULTURE'
  },
  {
    label: 'Banking',
    value: 'BANKING'
  },
  {
    label: 'Consumer',
    value: 'CONSUMER'
  },
  {
    label: 'Education',
    value: 'EDUCATION'
  },
  {
    label: 'Engineering',
    value: 'ENGINEERING'
  },
  {
    label: 'Energy',
    value: 'ENERGY'
  },
  {
    label: 'Oil and Gas',
    value: 'OIL_AND_GAS'
  },
  {
    label: 'Fast moving consumer goods',
    value: 'FAST_MOVING_CONSUMER_GOODS'
  },
  {
    label: 'Financial',
    value: 'FINANCIAL'
  },
  {
    label: 'Fintech',
    value: 'FINTECH'
  },
  {
    label: 'Food and Beverage',
    value: 'FOOD_AND_BEVERAGE'
  },
  {
    label: 'Government',
    value: 'GOVERNMENT'
  },
  {
    label: 'Healthcare',
    value: 'HEALTHCARE'
  },
  {
    label: 'Hospitality',
    value: 'HOSPITALITY'
  },
  {
    label: 'Insurance',
    value: 'INSURANCE'
  },
  {
    label: 'Legal',
    value: 'LEGAL'
  },
  {
    label: 'Manufacturing',
    value: 'MANUFACTURING'
  },
  {
    label: 'Media',
    value: 'MEDIA'
  },
  {
    label: 'Online',
    value: 'ONLINE'
  },
  {
    label: 'Raw Materials',
    value: 'RAW_MATERIALS'
  },
  {
    label: 'Real Estate',
    value: 'REAL_ESTATE'
  },
  {
    label: 'Religion',
    value: 'RELIGION'
  },

  {
    label: 'Retail',
    value: 'RETAIL'
  },
  {
    label: 'Jewelry',
    value: 'JEWELRY'
  },
  {
    label: 'Technology',
    value: 'TECHNOLOGY'
  },
  {
    label: 'Telecommunications',
    value: 'TELECOMMUNICATIONS'
  },
  {
    label: 'Transportation',
    value: 'TRANSPORTATION'
  },
  {
    label: 'Travel',
    value: 'TRAVEL'
  },
  {
    label: 'Electronics',
    value: 'ELECTRONICS'
  },
  {
    label: 'Not for profit',
    value: 'NOT_FOR_PROFIT'
  }
]

export const businessVerticals = [
  {
    label: 'Agriculture',
    value: 'AGRICULTURE'
  },
  {
    label: 'Communication',
    value: 'COMMUNICATION'
  },
  {
    label: 'Construction',
    value: 'CONSTRUCTION'
  },
  {
    label: 'Education',
    value: 'EDUCATION'
  },
  {
    label: 'Energy',
    value: 'ENERGY'
  },
  {
    label: 'Entertainment',
    value: 'ENTERTAINMENT'
  },
  {
    label: 'Financial',
    value: 'FINANCIAL'
  },
  {
    label: 'Gambling',
    value: 'GAMBLING'
  },
  {
    label: 'Government',
    value: 'GOVERNMENT'
  },
  {
    label: 'Healthcare',
    value: 'HEALTHCARE'
  },
  {
    label: 'Hospitality',
    value: 'HOSPITALITY'
  },
  {
    label: 'Human Resources',
    value: 'HUMAN_RESOURCES'
  },
  {
    label: 'Insurance',
    value: 'INSURANCE'
  },
  {
    label: 'Legal',
    value: 'LEGAL'
  },
  {
    label: 'Manufacturing',
    value: 'MANUFACTURING'
  },
  {
    label: 'NGO',
    value: 'NGO'
  },
  {
    label: 'Political',
    value: 'POLITICAL'
  },
  {
    label: 'Postal',
    value: 'POSTAL'
  },
  {
    label: 'Professional',
    value: 'PROFESSIONAL'
  },
  {
    label: 'Real Estate',
    value: 'REAL_ESTATE'
  },
  {
    label: 'Retail',
    value: 'RETAIL'
  },
  {
    label: 'Technology',
    value: 'TECHNOLOGY'
  },
  {
    label: 'Transportation',
    value: 'TRANSPORTATION'
  }
];


export function generateRandomPassword(length: number = 12) {
  let chars = '0123456789abcdefghijklmnopqrstuvwxyz@&ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let password = '';
  while (true) {
    password = '';
    const array = new Uint32Array(chars.length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += chars[array[i] % chars.length];
    }
    let upperCase = /[A-Z]+/.test(password)
    let lowerCase = /[a-z]+/.test(password)
    let number = /[0-9]+/.test(password)
    if(upperCase && lowerCase && number){
      break;
    }else{
      console.log('password not valid')
      continue;
    }
  }

  return password;
}

export function generateOtp(length: number = 4) {
  let chars = '0123456789';
  let otp = '';
  const array = new Uint32Array(chars.length);
  window.crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    otp += chars[array[i] % chars.length];
  }
  return otp;
}

export function getShareLink(shareId: string, referralId?: string) {
  let link = ''
  switch (config.mode) {
    case 'production': {
      link = `https://affiliates.gohighlevel.com/?fp_ref=${referralId}&share=${shareId}`
      break
    }
    case 'dev': {
      link = `http://localhost:8080/settings/snapshot/import/${shareId}`
      break
    }
    case 'staging': {
      link = `https://affiliate-dot-highlevel-staging.appspot.com/?fp_ref=${referralId}&share=${shareId}`
      break
    }
    default: {
      link = `https://${window.location.hostname}/settings/snapshot/import/${shareId}`
    }
  }

  return link
}

export function convertObjKeysToSnakeRecursively(
  object: string,
  skipKeysFor?: string[]
) {
  if (isArray(object)) {
    return object.map(i => {
      return convertObjKeysToSnakeRecursively(i)
    })
  }
  else if (isObject(object)) {
    const n = {}
    Object.keys(object).forEach(k => {
      if(skipKeysFor && skipKeysFor.includes(k)) {
        n[snakeCase(k)] = null;
        return;
      }
      n[snakeCase(k)] = convertObjKeysToSnakeRecursively(object[k])
    })
    return n
  }
  return object
}

function formatObjectWithLocationsId(object) {
  const result = {};
  if(object)
  {
    Object.keys(object).map((key: string)=> {
    Object.assign(result,{[key]: convertObjKeysToSnakeRecursively(object[key])});
  })
}
  return result;
}

export function transformUserApiData(userApiData) {
  const userData = convertObjKeysToSnakeRecursively(userApiData, [
    'locationWiseOpenHours',
    'locations',
    'userCalendar',
    'locationWiseTimezone',
    'locationWiseMeetingLocation',
    'locationWiseZoomAdded',
  ])
  Object.assign(userData, {
    role: userData?.roles?.role,
    type: userData?.roles?.type,
    last_name_lower_case: userData?.last_name?.toLowerCase(),
    email_lower_case: userData?.email?.toLowerCase(),
    first_name_lower_case: userData?.first_name?.toLowerCase(),
    locations: formatObjectWithLocationsId(userApiData.locations),
    user_calendar: formatObjectWithLocationsId(userApiData.userCalendar),
    location_wise_open_hours: formatObjectWithLocationsId(userApiData.locationWiseOpenHours),
    location_wise_timezone: formatObjectWithLocationsId(userApiData.locationWiseTimezone),
    location_wise_meeting_location: formatObjectWithLocationsId(userApiData.locationWiseMeetingLocation),
    location_wise_zoom_added: formatObjectWithLocationsId(userApiData.locationWiseZoomAdded),
    location_wise_day_of_the_weekwise_availability: formatObjectWithLocationsId(userApiData.locationWiseDayOfTheWeekwiseAvailability)
  })
  return userData;
}

export function transformLocationApiData(locationApiData) {
  const locObj = convertObjKeysToSnakeRecursively(locationApiData.data)
  Object.assign(locObj, {
    gmb_business_scope_exist: locationApiData.gmbMessagingScopeExist,
    name_lower_case: locationApiData.name.toLowerCase()
  })
  return locObj;
}

export const formatPhoneNumberE164 = (phone: string, countryCode?: string) =>
  phoneUtil.format(
    phoneUtil.parse(phone, countryCode),
    libphonenumber.PhoneNumberFormat.E164
  )